<template>
  <div
    :key="componentKey"
    v-click-outside="{
      handler: hideCalendar,
      closeConditional: () => positionContext,
    }"
    :class="[
      $style['date-picker__container'],
      'vgp-interactive-element',
      $style[customClass]
    ]"
  >
    <vgp-input
      ref="datePickerInput"
      :class="[
        !!positionContext ? $style.active : '',
        $style['custom-input-hover']
      ]"
      :model-value="formattedDate"
      :icons-left="iconsLeft"
      :border="false"
      :mask="dateMask"
      :readonly="isTimePicker"
      :is-disabled="disabled"
      :placeholder="placeholder"
      :input-size="'28px'"
      small
      @onFocus="showCalendar"
      @keydown.native.tab="hideCalendar"
      @onChange="handleChange"
    />

    <div
      v-if="positionContext && !isTimePicker"
      :class="$style.date_picker_container"
      :style="{
        top: coordinates.top,
        left: coordinates.left,
      }"
    >
      <vgp-date-picker
        v-if="property"
        :value="value"
        :start-or-end="property"
        :work-days="workDays"
        :min-date="minDate"
        :calendar-config="calendarConfig"
        @onChange="selectDate"
      >
        <template #footer="{ setValue }">
          <button
            v-if="!!todayDate"
            class="time-picker_label float-right"
            @click.stop="() => setValue(todayDate)"
          >
            {{ locales('datepiker_today') }}
          </button>
        </template>
      </vgp-date-picker>

      <vgp-date-picker
        v-if="!property"
        :value="value"
        :is-custom-date-column="true"
        :reset-button="true"
        :selected-date="!!value"
        @onChange="selectDate"
      >
        <template #footer="{ setValue }">
          <button
            class="time-picker_label float-right"
            @click.stop="() => setValue(new Date())"
          >
            {{ locales('datepiker_today') }}
          </button>
        </template>
      </vgp-date-picker>
    </div>

    <context-menu
      v-if="positionContext && isTimePicker"
      :position="positionContext"
    >
      <template #body>
        <webix-ui
          :config="datepickerConfig"
        />
      </template>
    </context-menu>
  </div>
</template>

<script>
import { throttle } from 'lodash';
import dateHelper from '$$helpers/dateFormats';
import moment from 'moment';
import constants from '$$helpers/constants';
import calendarModule from '../../../../../components/calendar';
import CalendarWorker from '../../../../../components/workers/CalendarWorker';
import globalToast from '$$vueCmp/toast';
import vgpDatePicker from '$$vueCmp/datePicker/vgpDatePicker.vue';

export default {
  name: 'DatePicker',
  components: {
    vgpDatePicker,
  },
  props: {
    property: {
      type: String,
      default: 'start_date',
    },
    placeholder: {
      type: String,
      default: '',
    },
    value: {
      type: [Date, String, Number],
      default: null,
    },
    type: {
      type: String,
      default: 'day',
    },
    calendarIcon: {
      type: Boolean,
      default: true,
    },
    disabled: {
      type: Boolean,
      default: false,
    },
    scrollingContainer: {
      type: String,
      default: null,
    },
    taskData: {
      type: Object,
      required: true,
    },
    allowEmpty: {
      type: Boolean,
      default: false,
    },
    calendarConfig: {
      type: Object,
      default: null,
    },
    customClass: {
      type: String,
      default: '',
    },
  },
  emits: [
    'onChange',
    'showCalendar',
  ],
  data() {
    return {
      locales: __,
      scrollHandler: null,
      resizeHandler: null,
      componentKey: 0,
      positionContext: null,
      coordinates: {
        left: 'auto',
        top: 'auto',
      },
    };
  },
  computed: {
    minDate() {
      return this.property === 'end_date' ? this.taskData.start_date : null;
    },
    todayDate() {
      const minDate = this.minDate;
      let result = null;

      if ((minDate ? minDate < new Date() : true) && calendarModule.isWorkDay(new Date(), this.calendarConfig)) {
        result = this.property === 'end_date' ? dateHelper.createDayStartedAt00(new Date(new Date().getTime() + 24 * 60 * 60 * 1000)) : dateHelper.createDayStartedAt00(new Date());
      }

      return result;
    },
    workDays() {
      const daysConfig = this.calendarConfig?.dates;

      return daysConfig ? Object.keys(daysConfig).filter(key => daysConfig[key]) : [];
    },
    dateMask() {
      if (this.isTimePicker) {
        return '';
      }

      return user.dateFormat.replace(/[a-z]/gi, '#');
    },
    iconsLeft() {
      if (this.calendarIcon) {
        return [
          {
            name: 'calendar',
            handler: this.focusDatePickerInput,
          },
        ];
      }

      return [];
    },
    isTimePicker() {
      return this.type === 'time';
    },
    formattedDate() {
      if (!this.value) return '';
      const date = new Date(this.value);
      let format = dateHelper.getDateFormat();

      if (this.isTimePicker) {
        format = dateHelper.getDateFormatForGanttGrid(true);
      }

      return webix.Date.dateToStr(format)(date);
    },
    datePickerId() {
      return `datePicker-${new Date().getTime()}`;
    },
    datepickerConfig() {
      const component = this;
      const date = this.value || null;
      const taskData = this.taskData;
      const config = {
        view: 'calendar',
        css: 'webix_calendar',
        id: component.datePickerId,
        monthSelect: true,
        navigation: true,
        weekHeader: true,
        borderless: true,
        fillspace: true,
        height: 260,
        type: this.type,
        calendar: {
          height: 300,
          cellHeight: 24,
        },
        date,
        icons: [{
          template() {
            const todayBtn = `<span role='button' tabindex='0' class='webix_cal_icon_today webix_cal_icon'>${webix.i18n.calendar.today}</span>`;

            return component.isShowTodayBtn() ? todayBtn : '';
          },
          on_click: {
            webix_cal_icon_today() {
              let dataForWorker;

              if (component.property === 'end_date') {
                dataForWorker = {
                  date: dateHelper.createDayStartedAt00(new Date(new Date().getTime() + 24 * 60 * 60 * 1000)),
                  dir: 'past',
                  task: taskData,
                };
              } else {
                dataForWorker = {
                  date: dateHelper.createDayStartedAt00(new Date()),
                  dir: 'future',
                  task: taskData,
                };
              }

              CalendarWorker.calculate(dataForWorker, 'getClosestWorkTime')
                .then(closestDate => {
                  this.setValue(closestDate);
                })
                .finally(() => {
                  this.callEvent('onTodaySet', [this.getSelectedDate()]);
                });
            },
            webix_cal_icon_clear() {
              this.setValue('');
              this.callEvent('onDateClear', []);
            },
          },
        }],
        on: {
          onChange(val) {
            component.selectDate(val);
          },
        },
        weekNumber: false,
        blockDates: component.isBlockedDate,
        blockTime: component.isBlockedTime,
        minuteStep: 1,
      };

      return config;
    },

  },
  watch: {
    positionContext(val) {
      if (val) {
        this.$nextTick(this.setSelected);
        this.$nextTick(this.calcCoordinates);
      }

      this.$emit('showCalendar', !!val);
    },
    disabled(val) {
      if (val) {
        this.positionContext = null;
      }
    },
  },
  mounted() {
    this.addScrollHandler();
    this.addResizeHandler();
  },
  beforeUnmount() {
    this.removeScrollHandler();
    this.removeResizeHandler();
  },
  methods: {
    calcCoordinates() {
      const coordinates = this.$refs.datePickerInput.$el.getBoundingClientRect();
      const clientHeight = document.documentElement.clientHeight;
      const clientWidth = document.documentElement.clientWidth;
      const elementHeight = 310;
      const elementWidth = 258;

      const dropToBottom = (coordinates.bottom + elementHeight) < clientHeight;
      const dropToRight = (coordinates.left + elementWidth) < clientWidth;

      if (dropToBottom) {
        this.coordinates = {
          top: `${coordinates.bottom + 2}px`,
          left: `${coordinates.left}px`,
        };
      } else {
        this.coordinates = {
          top: `${coordinates.top - elementHeight - 3}px`,
          left: `${coordinates.left}px`,
        };
      }

      if (!dropToRight) {
        this.coordinates.left = `${coordinates.left - elementWidth + coordinates.width + 2}px`;
      }
    },
    addScrollHandler() {
      this.scrollHandler = throttle(this.hideCalendar, 100);
      const scrollDivs = [
        ...document.querySelectorAll('div.gantt_scroll'),
        ...document.querySelectorAll('div.vgp_scroll_bar_container .simplebar-content-wrapper'),
      ];

      scrollDivs.forEach(div => div.addEventListener('scroll', this.scrollHandler));
    },
    addResizeHandler() {
      this.resizeHandler = throttle(this.adjustCalendarPopup, 100);
      window.addEventListener('resize', this.resizeHandler);
    },
    removeScrollHandler() {
      const scrollDivs = [
        ...document.querySelectorAll('div.gantt_scroll'),
        ...document.querySelectorAll('div.vgp_scroll_bar_container .simplebar-content-wrapper'),
      ];

      scrollDivs.forEach(div => div.removeEventListener('scroll', this.scrollHandler));
    },
    removeResizeHandler() {
      window.removeEventListener('resize', this.resizeHandler);
    },
    adjustCalendarPopup() {
      if (this.positionContext) {
        this.positionContext = this.$refs.datePickerInput.$el.getBoundingClientRect();
      }
    },
    focusDatePickerInput() {
      this.$refs.datePickerInput.$refs.input_field_icons.focus();
    },
    handleChange(event) {
      if (this.isTimePicker) {
        return;
      }

      const value = event.target.value;

      if (!value.length && this.allowEmpty) {
        this.selectDate();

        return;
      }

      if (!this.isValidManualDateInput(value)) {
        this.componentKey++;
        this.$nextTick(this.setSelected);
        globalToast.warning(__('gantt_date_invalid'));

        return;
      }

      const { day, month, year } = this.parseDayMonthYearFromManualDateInput(value);
      const newDate = new Date(year, month - 1, day);

      if (this.isBlockedDate(newDate)) {
        this.componentKey++;
        this.$nextTick(this.setSelected);

        return;
      }

      this.selectDate(newDate);
    },
    isValidManualDateInput(value) {
      if (value.length !== this.dateMask.length) return false;

      const { day, month, year } = this.parseDayMonthYearFromManualDateInput(value);

      if (month < 1 || month > 12) return false;
      const lastDayInMonth = (new Date(year, month, 0)).getDate();

      if (day > lastDayInMonth || day < 1) return false;

      return true;
    },
    parseDayMonthYearFromManualDateInput(value) {
      let day; let month; let
        year;
      const delimiter = dateHelper.getCurrentDateDelimiter();

      if (dateHelper.isCurrentDateFormatUSA()) {
        [month, day, year] = value.split(delimiter);
      } else {
        [day, month, year] = value.split(delimiter);
      }

      if (dateHelper.isCurrentDateFormatHas2DigitYear()) {
        year = `20${year}`;
      }

      return { day: Number(day), month: Number(month), year: Number(year) };
    },
    showCalendar() {
      if (this.disabled) return;

      this.positionContext = this.$refs.datePickerInput.$el.getBoundingClientRect();
    },
    hideCalendar() {
      this.positionContext = null;
      this.$refs.datePickerInput?.$refs.input_field_icons.blur();
    },
    selectDate(val = '') {
      const value = Array.isArray(val) ? val[0] : val;

      if ((new Date(value)).getTime() === (new Date(this.value)).getTime()) return;

      const emitValue = value ? this.toStringDate(value) : '';

      this.$emit('onChange', emitValue);
      this.hideCalendar();
    },

    toStringDate(date) {
      return moment(date, constants.TASK_DATES_FORMAT).toDate();
    },

    setSelected() {
      const $$ui = $$(this.datePickerId);
      const date = this.value;

      $$ui && date && $$ui.selectDate(date);
    },
    isShowTodayBtn() {
      const isWorkToday = !this.isBlockedDate(new Date());

      if (this.property === 'start_date') {
        return isWorkToday;
      }

      if (this.property === 'end_date') {
        const isTodayAfterStartDate = dateHelper.createDayStartedAt00(new Date(_.clone(this.taskData.start_date))).valueOf() < new Date().valueOf();

        return isTodayAfterStartDate && isWorkToday;
      }

      return true;
    },
    isBlockedDate(date) {
      if (this.property !== 'start_date' && this.property !== 'end_date') return false;

      if (!this.calendarConfig) {
        return true;
      }

      if (this.property === 'end_date') {
        if (new Date(date).valueOf() < dateHelper.createDayStartedAt00(_.clone(this.taskData.start_date)).valueOf()) {
          return true;
        }
      }

      if (calendarModule.isWorkDay(date, this.calendarConfig)) return false;

      return true;
    },

    isBlockedTime(date) {
      if (this.property !== 'start_date' && this.property !== 'end_date') return false;

      if (!this.calendarConfig) {
        return true;
      }

      if (this.property === 'end_date' && calendarModule.isWorkEndTime(date, this.calendarConfig)) {
        return false;
      }

      if (this.property === 'start_date' && calendarModule.isWorkStartTime(date, this.calendarConfig)) {
        return false;
      }

      return true;
    },
  },
};
</script>

<style module lang="less">

@import '../../../../../less/_mixins.less';
@import '../../../../../less/_variables.less';

.date-picker__container {
  width: 100%;
}

.active {
  .border(2px, @blue-default) !important;
}

.custom-input-hover:hover {
  background-color: #f8f8f8;
}

.board_card_date_picker {
  .active {
    box-shadow: unset !important;
    outline: 2px solid #4484CD !important;
    outline-offset: -2px;
  }
}

.date_picker_container {
  position: fixed;
  z-index: 5;
  box-shadow: 0px 9px 22px -2px #00000033;
}

</style>
